import * as _ from 'lodash';
import { AfterViewInit, Component, computed, EventEmitter, HostListener, inject, input, OnDestroy, OnInit, output, Output, OutputEmitterRef, Signal, signal } from '@angular/core';

import { _FormModelService } from '../../services/model-services/_form-model.service';
import { _SectionModelComponent as _SectionModelComponent } from './_section-model-component';
import { IImageParentData } from 'src/app/features/image/models/image-parent-data';
import { ILevelGeneralData } from '../../models/level-general-data';
import { _PageService } from '../../services/page-services/_page.service';
import { IImageDto } from 'src/assets/common/models/image-dto.g';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { pageMode } from '../../models/page-mode';
import { ActivatedRouteService } from '../../services/route-service/activated-route.service';
import { AbstractControl, FormControl } from '@angular/forms';
import { eventFromOverlay } from 'src/app/infrastructure/functions/event-from-overlay.function';

@Component({
    selector: 'app-form-section',
    template: '',
    styles: []
})
export abstract class _SectionFormComponent<DataModel extends ILevelGeneralData>
    extends _SectionModelComponent<DataModel>
    implements AfterViewInit, OnInit
{
    protected readonly abstract _activatedRouteService: ActivatedRouteService;
    protected readonly abstract _pageService: _PageService;
    protected readonly abstract _centralPageService: _CentralPageService;
    protected readonly abstract _formModelService: _FormModelService;

    abstract form$$: Signal<AbstractControl<DataModel> | null>;
    abstract formControls$$: Signal<{[key in keyof DataModel]: AbstractControl<DataModel[key]>} | null>;
    abstract showError$$: Signal<boolean>;

    abstract autoFocus$$: Signal<boolean>;

    abstract out_onDirty$$: OutputEmitterRef<boolean>;
    abstract out_onEnter$$: OutputEmitterRef<void>;
    abstract out_onFormReset$$: OutputEmitterRef<void>;

    abstract emit_onDirty(dirty: boolean): void;
    abstract emit_onEnter(): void;
    abstract form_reset(): void;

    protected _input_AutoFocus$$ = input(false, {alias: 'autofocus'})

    protected _out_onDirty = output<boolean>({alias: 'onDirty'});
    protected _out_onEnter = output({alias: 'onEnter'});
    protected _out_onFormReset = output({alias: 'onFormReset'});

    protected _form$$ = computed(() => this._formModelService.form$$());
    protected _formControls$$ = computed<{[key in keyof DataModel]: FormControl<DataModel[key]>}>(
        () => (this._form$$()?.controls as {[key in keyof DataModel]: FormControl<DataModel[key]>}) ?? null
    );
    protected _showError$$ = signal<boolean>(false);

    @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
        const __form_dirty = this._form$$()?.dirty;
        const __pageMode = this._centralPageService.pageMode$$();
        const __pageLayout = this._centralPageService.pageLayout$$();
        const __pageMode_prev = this._centralPageService.pageMode_prev$$();

        // Esc in een dialoog/panel hoort bij die overlay, niet bij de pagina.
        if (event.key === 'Escape' && !eventFromOverlay(event)) {
            if (__form_dirty) {
                event.stopPropagation();
                this.__form_resetFromDirty();
            } else if (__pageMode === pageMode.editPage) {
                const _newPageMode = __pageMode_prev ?? pageMode.detailsPage;
                this._centralPageService.pageMode_set(_newPageMode);
            } else if (__pageMode === pageMode.detailsPage && __pageLayout === 'tab-page' && this._centralPageService.hasTabs$$()) {
                this._centralPageService.pageMode_set(pageMode.tabPage);
            } else {
                this.emit_onEscape();
            }
        }
    }

    protected override _ngOnInit(): void {
        super._ngOnInit();

        const _queryParams = this._activatedRouteService.queryParams ?? {};
        this._form$$()?.patchValue(_queryParams);
    }

    private __form_resetFromDirty(): void {
        this._formModelService.form_resetFromDirty();
    }

    protected _emit_onDirty(dirty: boolean): void {
        // Validatiefouten (mat-error onder het veld) pas tonen zodra de gebruiker daadwerkelijk
        // iets wijzigt — anders staat een leeg/nog-niet-ingevuld verplicht veld meteen rood bij
        // het openen van het formulier. dirty blijft true na de eerste wijziging (Angular reset
        // 'm niet vanzelf), dus dit is een eenmalige aan-knop tot de volgende form_reset().
        this._showError$$.set(dirty);
        this._out_onDirty.emit(dirty);
    }

    protected _emit_onEnter(): void {
        this._out_onEnter.emit();
    }

    protected _form_reset(): void {
        this._showError$$.set(false);
        this._out_onFormReset.emit();
    }
}
